/**
 * Reports Landing Page
 * Shows report categories and individual reports
 */

import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  BarChart3, 
  Clock, 
  TrendingUp,
  Users,
  Church,
  ArrowRight,
  Lock
} from "lucide-react";

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: typeof FileText;
  path: string;
  isAvailable: boolean;
}

const credentialReports: ReportCard[] = [
  {
    id: "ministers-list",
    title: "Lista Completa de Ministros",
    description: "Listado detallado de todos los ministros incluidos en solicitudes de credenciales con filtros avanzados",
    icon: FileText,
    path: "/reports/credentials/ministers-list",
    isAvailable: true,
  },
  {
    id: "by-degree",
    title: "Ministros por Grado",
    description: "Distribución y análisis estadístico de ministros agrupados por grado ministerial",
    icon: BarChart3,
    path: "/reports/credentials/by-degree",
    isAvailable: false,
  },
  {
    id: "timeline",
    title: "Línea de Tiempo",
    description: "Análisis temporal de solicitudes de credenciales y tendencias de aprobación",
    icon: TrendingUp,
    path: "/reports/credentials/timeline",
    isAvailable: false,
  },
  {
    id: "processing-times",
    title: "Tiempos de Procesamiento",
    description: "Análisis de tiempos promedio entre estados y eficiencia por distrito",
    icon: Clock,
    path: "/reports/credentials/processing-times",
    isAvailable: false,
  },
];

const ministerialReports: ReportCard[] = [
  {
    id: "registry",
    title: "Padrón General de Ministros",
    description: "Todos los ministros activos e inactivos con estado de credencial, razón de inactividad y color por estado",
    icon: Users,
    path: "/reports/ministers/registry",
    isAvailable: true,
  },
  {
    id: "transfers",
    title: "Movimientos Territoriales",
    description: "Traslados de ministros entre distritos y regiones con flujo de entradas y salidas por distrito",
    icon: TrendingUp,
    path: "/reports/ministers/transfers",
    isAvailable: true,
  },
  {
    id: "degree-changes",
    title: "Ascensos de Grado",
    description: "Cambios de grado ministerial registrados con resumen de promociones por grado",
    icon: BarChart3,
    path: "/reports/ministers/degree-changes",
    isAvailable: true,
  },
];

const churchReports: ReportCard[] = [
  {
    id: "church-overview",
    title: "Reporte de Iglesias",
    description: "Estadísticas y análisis de iglesias por región",
    icon: Church,
    path: "/reports/churches/overview",
    isAvailable: false,
  },
];

export function ReportsLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Reportes</h1>
        <p className="mt-2 text-muted-foreground">
          Análisis y exportación de datos del sistema
        </p>
      </div>

      {/* Credential Reports */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <FileText className="size-5" />
          <h2 className="text-xl font-semibold">Reportes de Credenciales</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          {credentialReports.map((report) => (
            <ReportCardComponent
              key={report.id}
              report={report}
              onClick={() => navigate(report.path)}
            />
          ))}
        </div>
      </section>

      {/* Ministerial Reports */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <Users className="size-5" />
          <h2 className="text-xl font-semibold">Reportes Ministeriales</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          {ministerialReports.map((report) => (
            <ReportCardComponent
              key={report.id}
              report={report}
              onClick={() => navigate(report.path)}
            />
          ))}
        </div>
      </section>

      {/* Church Reports */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <Church className="size-5" />
          <h2 className="text-xl font-semibold">Reportes de Iglesias</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Próximamente</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          {churchReports.map((report) => (
            <ReportCardComponent
              key={report.id}
              report={report}
              onClick={() => navigate(report.path)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

interface ReportCardComponentProps {
  report: ReportCard;
  onClick: () => void;
}

function ReportCardComponent({ report, onClick }: ReportCardComponentProps) {
  const Icon = report.icon;

  return (
    <Card 
      className={`transition-all ${
        report.isAvailable
          ? "cursor-pointer hover:shadow-md"
          : "cursor-not-allowed opacity-60"
      }`}
      onClick={report.isAvailable ? onClick : undefined}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`rounded-lg p-3 ${
              report.isAvailable ? "bg-primary/10" : "bg-muted"
            }`}>
              <Icon className={`size-6 ${
                report.isAvailable ? "text-primary" : "text-muted-foreground"
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{report.title}</h3>
                {!report.isAvailable && (
                  <Lock className="size-4 text-muted-foreground" />
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {report.description}
              </p>
              {report.isAvailable && (
                <Button variant="ghost" size="sm" className="mt-3 px-0">
                  Ver reporte
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
